// src/components/nutrition/fats/nutritionFats.jsx
// Fats module: options, initial data, grouping, overview meta, statuses,
// focus entries, special tiles, chart transformer, and snapshot → UI data
// builder. All user-specific numbers are derived from the
// nutritionTargetsCalculations snapshot (no hard-coded intake values).

// ---------------------------------------------------------------------------
// Public options
// ---------------------------------------------------------------------------

export const fatOptions = [
  // Scored items (interpreted via NNR/WHO-style energy-percent targets)
  { id: "ala", label: "ALA (α-linolenic acid)" },
  { id: "mufa", label: "Monounsaturated Fat (MUFA)" },
  { id: "n3Total", label: "n-3 PUFA (Total)" },
  { id: "pufa", label: "Polyunsaturated Fat (PUFA)" },
  { id: "sfa", label: "Saturated Fat (SFA)" },

  // Info-only (not scored in donut)
  { id: "omega3", label: "EPA+DHA (info)" },
  { id: "tfa", label: "Trans Fat (TFA)" },
  { id: "totalFat", label: "Total Fat" },
];

// Initial skeleton only. No example E% or mg values here – everything that
// depends on life stage or intake will be filled by the snapshot builder.
export const fatInitialData = {
  // Scored chart items (E%). targetMin/targetMax are guideline metadata,
  // not user data. They can later be overridden from Firestore or snapshot.
  ala: { ePct: null, avgEPct: null, targetMin: 0.5, targetMax: null },
  mufa: { ePct: null, avgEPct: null, targetMin: 10, targetMax: 20 },
  n3Total: { ePct: null, avgEPct: null, targetMin: 1, targetMax: null },
  pufa: { ePct: null, avgEPct: null, targetMin: 5, targetMax: 10 },
  sfa: { ePct: null, avgEPct: null, targetMin: null, targetMax: 10 },

  // Info-only
  omega3: {
    epa: null,
    dha: null,
    total: null,
    goal: 500, // global guideline; can be overridden per life stage if available
    avgTotal: null,
  },
  tfa: {
    ePct: null,
    note: "As low as possible",
  },
  totalFat: { ePct: null, avgEPct: null, targetMin: 25, targetMax: 40 },
};

// ---------------------------------------------------------------------------
// Internal helpers and guideline configuration
// ---------------------------------------------------------------------------

const FAT_STATUS_CONFIG = {
  // These ranges come from NNR/WHO-style guidance, not from user data.
  ala: { kind: "min", min: 0.5 },
  mufa: { kind: "range", min: 10, max: 20 },
  n3Total: { kind: "min", min: 1 },
  pufa: { kind: "range", min: 5, max: 10 },
  sfa: { kind: "max", max: 10 },
  totalFat: { kind: "range", min: 25, max: 40 },
};

// Default scores used only when we have no calculated E% yet.
const FAT_FOCUS_DEFAULT_SCORES = {
  ala: 0.5,
  mufa: 15,
  n3Total: 1,
  pufa: 7.5,
  sfa: 10,
  totalFat: 30,
};

const FAT_CHART_IDS = ["ala", "mufa", "n3Total", "pufa", "sfa", "totalFat"];

const FAT_LABELS = {
  ala: "ALA",
  mufa: "Monounsaturated Fat",
  n3Total: "n-3 PUFA (Total)",
  pufa: "Polyunsaturated Fat",
  sfa: "Saturated Fat",
  totalFat: "Total Fat",
};

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

// Map an E% for a given fat to a pseudo-%DV so the ring can behave like other
// nutrients. For ranges we aim for the midpoint; for min/max we aim for the
// threshold value.
function computeFatDvPercentFromEnergy(fatId, energyPercent) {
  if (!isFiniteNumber(energyPercent) || energyPercent < 0) {
    return null;
  }

  const config = FAT_STATUS_CONFIG[fatId];
  if (!config) {
    return null;
  }

  let target = null;

  if (
    config.kind === "range" &&
    isFiniteNumber(config.min) &&
    isFiniteNumber(config.max)
  ) {
    target = (config.min + config.max) / 2;
  } else if (config.kind === "min" && isFiniteNumber(config.min)) {
    target = config.min;
  } else if (config.kind === "max" && isFiniteNumber(config.max)) {
    target = config.max;
  }

  if (!isFiniteNumber(target) || target <= 0) {
    return null;
  }

  const normalized = energyPercent / target;
  let dvPercent = Math.round(normalized * 100);

  if (!Number.isFinite(dvPercent)) {
    return null;
  }
  if (dvPercent < 0) dvPercent = 0;
  if (dvPercent > 200) dvPercent = 200;

  return dvPercent;
}

// ---------------------------------------------------------------------------
//// Grouping and overview
// ---------------------------------------------------------------------------

export function getFatsGroup() {
  return {
    name: "Fats",
    items: fatOptions.map((option) => ({
      id: option.id,
      label: option.label,
    })),
  };
}

export function getFatsOverviewMeta(data) {
  const totalFatNode = data?.totalFat || {};
  const totalFatEnergyPercent = isFiniteNumber(totalFatNode.ePct)
    ? totalFatNode.ePct
    : null;

  return {
    id: "fats",
    label: "Fats",
    meta:
      totalFatEnergyPercent != null
        ? `${Math.round(totalFatEnergyPercent)}% E (target 25–40)`
        : "–",
  };
}

// ---------------------------------------------------------------------------
// Statuses (life-stage aware via snapshot-driven E% + guideline ranges)
// ---------------------------------------------------------------------------

// Expect { ePctStatusFn } from nutritionData (min/max E% ranges).
export function getFatStatuses(data, { ePctStatusFn }) {
  const fatsData = data || {};
  const statuses = {};

  // Scored fats.
  Object.entries(FAT_STATUS_CONFIG).forEach(([fatId, config]) => {
    const node = fatsData[fatId] || {};
    const energyPercent = isFiniteNumber(node.ePct) ? node.ePct : null;

    if (energyPercent == null) {
      statuses[fatId] = "data-needed";
      return;
    }

    if (config.kind === "range") {
      statuses[fatId] = ePctStatusFn
        ? ePctStatusFn(energyPercent, config.min, config.max)
        : "on-track";
    } else if (config.kind === "min") {
      statuses[fatId] = energyPercent >= config.min ? "on-track" : "low";
    } else if (config.kind === "max") {
      statuses[fatId] = energyPercent >= config.max ? "high" : "on-track";
    } else {
      statuses[fatId] = "on-track";
    }
  });

  // Omega-3 EPA + DHA: info-only, simple goal comparison.
  const omega3Node = fatsData.omega3 || {};
  const totalOmega3 = isFiniteNumber(omega3Node.total) ? omega3Node.total : null;
  const omega3Goal = isFiniteNumber(omega3Node.goal) ? omega3Node.goal : 500;

  if (totalOmega3 == null) {
    statuses.omega3 = "data-needed";
  } else {
    statuses.omega3 = totalOmega3 >= omega3Goal ? "on-track" : "low";
  }

  // Trans fat: info-only — warn if present (>0.5% E threshold set as “caution”).
  const transFatNode = fatsData.tfa || {};
  const transFatEnergyPercent = isFiniteNumber(transFatNode.ePct)
    ? transFatNode.ePct
    : null;

  if (transFatEnergyPercent == null) {
    statuses.tfa = "data-needed";
  } else {
    statuses.tfa = transFatEnergyPercent > 0.5 ? "caution" : "on-track";
  }

  return statuses;
}

// ---------------------------------------------------------------------------
// Focus entries
// ---------------------------------------------------------------------------

export function getFatFocusEntries(data) {
  const fatsData = data || {};
  const entries = [];

  // Simple scoring proxy: current E% when available, otherwise a sensible default.
  Object.entries(FAT_FOCUS_DEFAULT_SCORES).forEach(([fatId, fallbackScore]) => {
    const node = fatsData[fatId] || {};
    const energyPercent = isFiniteNumber(node.ePct) ? node.ePct : null;
    const score = energyPercent != null ? energyPercent : fallbackScore;

    entries.push({ id: fatId, score });
  });

  // Do not include omega3/tfa (info-only) in focus sorting.
  return entries;
}

// ---------------------------------------------------------------------------
// Special tiles for info-only fats
// ---------------------------------------------------------------------------

export function buildFatSpecialTile(id, node, statuses, label) {
  // Omega-3 (EPA+DHA) — info-only.
  if (id === "omega3" && node) {
    const totalValue = isFiniteNumber(node.total) ? node.total : 0;
    const goal = isFiniteNumber(node.goal) ? node.goal : 500;

    const epaValue = isFiniteNumber(node.epa) ? node.epa : 0;
    const dhaValue = isFiniteNumber(node.dha) ? node.dha : 0;
    const averageTotal =
      isFiniteNumber(node.avgTotal) && node.avgTotal > 0
        ? node.avgTotal
        : totalValue;

    // Combine target and EPA/DHA summary into helper so we do not need a
    // "Show details" chevron; this lets the UI render the medical clipboard
    // button (actions.open) instead.
    const helperTextParts = [];

    if (goal && goal > 0) {
      helperTextParts.push(`Seafood target: ${goal} mg/day`);
    } else {
      helperTextParts.push("Seafood sources are the main contributors.");
    }

    helperTextParts.push(
      `EPA ${epaValue} mg • DHA ${dhaValue} mg • average ${averageTotal} mg/day`,
    );

    const helperText = helperTextParts.join(" • ");

    return {
      id,
      label,
      status: statuses[id],
      hero: {
        type: "value",
        value: totalValue,
        unit: "mg",
      },
      helper: helperText,
      // No details → no btn-outline chevron, the shell will show the
      // medical clipboard based on actions.open.
      details: null,
      actions: {
        open: { id, label },
        log: { mode: "dose", nutrientId: id, amount: 250, unit: "mg" },
      },
    };
  }

  // Trans fat — info-only.
  if (id === "tfa" && node) {
    const energyPercent = isFiniteNumber(node.ePct) ? node.ePct : 0;

    const helperText =
      "Keep as low as possible. No numeric DRV in NNR2023 (WHO uses ≤1% E as a reference).";

    return {
      id,
      label,
      status: statuses[id],
      hero: {
        type: "value",
        value: energyPercent,
        unit: "% E",
      },
      helper: helperText,
      // No details so we get the medical clipboard instead of "Show details".
      details: null,
      actions: {
        open: { id, label },
        log: null,
      },
    };
  }

  return null;
}

export function getFatLogAction(id) {
  if (id === "omega3") {
    return { mode: "dose", nutrientId: id, amount: 250, unit: "mg" };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Chart data (ONLY items with numeric style targets)
// ---------------------------------------------------------------------------

export function getFatChartData(data) {
  const fatsData = data || {};
  const lowFats = [];
  const onGoalFats = [];
  const highFats = [];

  FAT_CHART_IDS.forEach((id) => {
    const nutrient = fatsData[id];
    const energyPercent = isFiniteNumber(nutrient?.ePct) ? nutrient.ePct : null;
    if (energyPercent == null) return;

    const config = FAT_STATUS_CONFIG[id];
    let status = "on-goal";

    if (config?.kind === "range") {
      status =
        energyPercent < config.min
          ? "low"
          : energyPercent > config.max
          ? "high"
          : "on-goal";
    } else if (config?.kind === "min") {
      status = energyPercent < config.min ? "low" : "on-goal";
    } else if (config?.kind === "max") {
      status = energyPercent >= config.max ? "high" : "on-goal";
    }

    const label = FAT_LABELS[id];

    if (status === "low") {
      lowFats.push({ id, label, ePct: energyPercent });
    } else if (status === "high") {
      highFats.push({ id, label, ePct: energyPercent });
    } else {
      onGoalFats.push({ id, label, ePct: energyPercent });
    }
  });

  return {
    low: lowFats,
    onGoal: onGoalFats,
    high: highFats,
    category: "fats",
    categoryLabel: "Fats (E%)",
  };
}

// ---------------------------------------------------------------------------
// Calculate snapshot → fat data overrides
// ---------------------------------------------------------------------------

/**
 * Build fat overrides from the nutritionTargetsCalculations snapshot.
 * All user-specific values (grams and E%) are derived here from the
 * life-stage–aware snapshot. The caller merges this into effective data.
 */
export function buildFatDataFromNutritionCalculateSnapshot(
  snapshotOrIntakeSummary,
  existingData,
) {
  // Start from the initial fat metadata so we always have a full shape.
  const fatDataMap = createBaseFatDataFromInitialMeta();

  if (!snapshotOrIntakeSummary) {
    return mergeFatDataIntoExisting(existingData, fatDataMap);
  }

  const intakeSummary =
    snapshotOrIntakeSummary && snapshotOrIntakeSummary.intakeSummary
      ? snapshotOrIntakeSummary.intakeSummary
      : snapshotOrIntakeSummary;

  if (!intakeSummary || typeof intakeSummary !== "object") {
    return mergeFatDataIntoExisting(existingData, fatDataMap);
  }

  const { averageDailyIntake = {}, comparisonVsTargets = {} } = intakeSummary;

  const energyKilocaloriesRaw = averageDailyIntake.energy_kcal;
  const energyKilocalories = isFiniteNumber(energyKilocaloriesRaw)
    ? energyKilocaloriesRaw
    : null;

  // Convert grams of a fat to E% using total energy from the snapshot.
  const computeEnergyPercentFromGrams = (gramsValue) => {
    if (!energyKilocalories || !isFiniteNumber(gramsValue) || gramsValue <= 0) {
      return null;
    }
    const energyFromFat = gramsValue * 9; // 9 kcal per gram of fat
    const energyPercent = (energyFromFat / energyKilocalories) * 100;
    return Math.round(energyPercent * 10) / 10;
  };

  const applyFatEnergyPercent = (fatId, gramsKey) => {
    const gramsValue = averageDailyIntake[gramsKey];
    const energyPercent = computeEnergyPercentFromGrams(gramsValue);
    if (energyPercent == null) {
      return;
    }

    const existingNode = fatDataMap[fatId] || {};
    const pseudoDvPercent = computeFatDvPercentFromEnergy(fatId, energyPercent);

    fatDataMap[fatId] = {
      ...existingNode,
      ePct: energyPercent,
      avgEPct: energyPercent,
      ...(pseudoDvPercent != null
        ? { todayDV: pseudoDvPercent, avgDV: pseudoDvPercent }
        : {}),
    };
  };

  // E% for scored fats, all derived from snapshot grams + energy_kcal.
  applyFatEnergyPercent("totalFat", "fat_g");
  applyFatEnergyPercent("sfa", "saturates_g");
  applyFatEnergyPercent("pufa", "pufa_g");
  applyFatEnergyPercent("mufa", "mufa_g");
  applyFatEnergyPercent("n3Total", "n3_pufa_g");
  applyFatEnergyPercent("ala", "ala_g");
  applyFatEnergyPercent("tfa", "tfa_g");

  // If the backend provides a life-stage–specific percent-of-target for total
  // fat (fat_g), prefer that for todayDV/avgDV so the ring score matches other
  // nutrients that use percentOfTarget.
  const totalFatComparison = comparisonVsTargets.fat_g;
  if (totalFatComparison && isFiniteNumber(totalFatComparison.percentOfTarget)) {
    const usedPercent = Math.round(totalFatComparison.percentOfTarget);
    const existingTotalFatNode = fatDataMap.totalFat || {};
    fatDataMap.totalFat = {
      ...existingTotalFatNode,
      todayDV: usedPercent,
      avgDV: usedPercent,
    };
  }

  // EPA + DHA in mg (info-only, but fully dynamic).
  const epaMilligrams = averageDailyIntake.epa_mg;
  const dhaMilligrams = averageDailyIntake.dha_mg;

  if (isFiniteNumber(epaMilligrams) || isFiniteNumber(dhaMilligrams)) {
    const existingOmegaNode = fatDataMap.omega3 || {};
    const safeEpa = isFiniteNumber(epaMilligrams) ? epaMilligrams : 0;
    const safeDha = isFiniteNumber(dhaMilligrams) ? dhaMilligrams : 0;
    const totalOmegaMilligrams = safeEpa + safeDha;

    fatDataMap.omega3 = {
      ...existingOmegaNode,
      epa: isFiniteNumber(epaMilligrams)
        ? Math.round(epaMilligrams)
        : existingOmegaNode.epa ?? null,
      dha: isFiniteNumber(dhaMilligrams)
        ? Math.round(dhaMilligrams)
        : existingOmegaNode.dha ?? null,
      total:
        totalOmegaMilligrams > 0
          ? Math.round(totalOmegaMilligrams)
          : existingOmegaNode.total ?? null,
      avgTotal:
        totalOmegaMilligrams > 0
          ? Math.round(totalOmegaMilligrams)
          : existingOmegaNode.avgTotal ?? null,
      // Preserve any externally-configured goal if present; otherwise fall back
      // to the global guideline.
      goal:
        isFiniteNumber(existingOmegaNode.goal) && existingOmegaNode.goal > 0
          ? existingOmegaNode.goal
          : 500,
    };
  }

  // Attach rangeDays so fats behave like vitamins/minerals for range-aware logic.
  let rangeDays = null;
  if (
    snapshotOrIntakeSummary &&
    typeof snapshotOrIntakeSummary === "object" &&
    snapshotOrIntakeSummary.rangeInfo &&
    isFiniteNumber(snapshotOrIntakeSummary.rangeInfo.daysInRange)
  ) {
    rangeDays = snapshotOrIntakeSummary.rangeInfo.daysInRange;
  }

  if (rangeDays != null) {
    fatOptions.forEach((option) => {
      const fatId = option.id;
      const node = fatDataMap[fatId] || (fatDataMap[fatId] = {});
      node.rangeDays = rangeDays;
    });
  }

  const finalData = mergeFatDataIntoExisting(existingData, fatDataMap);
  return finalData;
}

// ---------------------------------------------------------------------------
// Snapshot merge helpers
// ---------------------------------------------------------------------------

function createBaseFatDataFromInitialMeta() {
  const result = {};

  fatOptions.forEach((option) => {
    const fatId = option.id;
    const initialNode = fatInitialData[fatId] || {};
    result[fatId] = { ...initialNode };
  });

  return result;
}

function mergeFatDataIntoExisting(existingData, newData) {
  if (!existingData || typeof existingData !== "object") {
    return newData;
  }

  const mergedData = existingData;

  fatOptions.forEach((option) => {
    const fatId = option.id;
    const newNode = newData[fatId] || {};
    const existingNode = existingData[fatId] || {};

    mergedData[fatId] = {
      ...existingNode,
      ...newNode,
    };
  });

  return mergedData;
}
